import moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import { AsYouType } from 'libphonenumber-js';

import * as api from '../../../../api';

import { Button } from '../../../../components/Button';
import { getPrevisitAsset } from '../../../../utils';
import { getProviderNameFull } from '../../../../utils/provider';
import { TIME_OF_DAY_FORMAT } from '../../../../utils/dateTime';
import { constructAddressSchedulingConfirmation } from '../../../../utils/practice';

import styles from '../index.module.css';

import { Alert, Typography, VStack } from '@vivaahealth/design-system';

interface Props {
  timeSlotConfirmations: (api.EhrTimeSlot.EhrTimeSlot &
    api.EhrTimeSlot.TimeSlotConfirmation)[];
  navigateToNextPage: () => Promise<void>;
  hasBookedAllAppointments: boolean;
}

const SeriesConfirmationPage: React.FC<Props> = ({
  timeSlotConfirmations,
  navigateToNextPage,
  hasBookedAllAppointments,
}) => {
  const { t } = useTranslation();
  const {
    providerFirstName,
    providerLastName,
    providerPrefix,
    providerSuffix,
    practiceName,
    address,
    city,
    state,
    zipCode,
    phoneNumber,
    previsitImageUrl,
  } = timeSlotConfirmations[0];
  const providerImageSrc = getPrevisitAsset(previsitImageUrl || '');

  const phoneNumberLink = (
    <a
      href={`tel:${phoneNumber}`}
      target="_blank"
      rel="noopener noreferrer"
      className={styles.PhoneLink}
    >
      {new AsYouType('US').input(phoneNumber)}
    </a>
  );

  return (
    <div>
      {hasBookedAllAppointments ? (
        <>
          <h3>
            <strong>You're all set!</strong>
          </h3>
          <Typography variant="title-medium">
            You've successfully scheduled the following appointments.
          </Typography>
        </>
      ) : (
        <Typography variant="title-medium">
          You've successfully scheduled the following appointments. Please call
          your provider to book your other appointments.
        </Typography>
      )}
      <div className={styles.CardWrapper}>
        <div className={styles.CardContent}>
          <div
            className={styles.ProviderCard}
            style={{ backgroundImage: `url(${providerImageSrc})` }}
          />
          <VStack gap={8}>
            <div className={styles.ProviderName}>
              {getProviderNameFull({
                provider: {
                  firstName: providerFirstName,
                  lastName: providerLastName,
                  prefix: providerPrefix,
                  suffix: providerSuffix,
                },
              })}
            </div>
            <div>
              {practiceName
                ? practiceName
                : constructAddressSchedulingConfirmation({
                    address,
                    city,
                    state,
                    zip: zipCode,
                  })}
            </div>
            <div>{phoneNumberLink}</div>
            {timeSlotConfirmations.map((timeSlot) => (
              <Typography
                variant="title-medium"
                bold
                key={timeSlot.id}
                data-test-id="series-confirmation-time-slot"
              >
                {moment(timeSlot.startTime)
                  .tz(timeSlot.timezone)
                  .format('MMM Do')}
                ,{' '}
                {moment(timeSlot.startTime)
                  .tz(timeSlot.timezone)
                  .format(TIME_OF_DAY_FORMAT)}
              </Typography>
            ))}
          </VStack>
        </div>
        <Alert variant="info">
          Call your provider at {phoneNumberLink} to cancel
          {hasBookedAllAppointments
            ? ' or reschedule.'
            : ', reschedule, or book additional appointments.'}
        </Alert>
      </div>
      <div className={styles.ButtonSubmitWrapper}>
        <Button
          type="button"
          children={t('Continue')}
          onClick={navigateToNextPage}
        />
      </div>
    </div>
  );
};

export default SeriesConfirmationPage;
